import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as XLSX from 'xlsx';
import { notify } from '../../common/functions/react-toastify.function';
import { updateFiberPopSite } from './functions/update_fiber_pop_site.function';

// Normalize header for matching: lowercase, collapse spaces/underscores
const normalizeHeader = (str) => {
  if (str == null || typeof str !== 'string') {
    return '';
  }
  return str
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '');
};

// Map normalized Excel header -> API/DB key (Excel can use any case, spaces or underscores)
const HEADER_TO_KEY = {
  fiberpop: 'fiber_pop',
  fiber_pop: 'fiber_pop',
  lateststatus: 'latest_status',
  latest_status: 'latest_status',
  status: 'latest_status',
  ranregion: 'ran_region',
  ran_region: 'ran_region',
  fooregion: 'foo_region',
  foo_region: 'foo_region',
  cityname: 'city_name',
  city_name: 'city_name',
  city: 'city_name',
  type: 'type',
  telecomregion: 'telecom_region',
  telecom_region: 'telecom_region',
  ransiteflag: 'ran_site_flag',
  ran_site_flag: 'ran_site_flag',
  ransite: 'ran_site_flag',
  olalocationflag: 'ola_location_flag',
  ola_location_flag: 'ola_location_flag',
  olalocation: 'ola_location_flag',
  sitename: 'site_name',
  site_name: 'site_name',
  site: 'site_name',
  technology: 'technology',
  vendor: 'vendor',
  route: 'route',
  latitude: 'latitude',
  lattitude: 'latitude',
  latt: 'latitude',
  lat: 'latitude',
  longitude: 'longitude',
  long: 'longitude',
  lng: 'longitude',
};

const normalizeRowToPayload = (row, headerMap) => {
  const payload = {};
  Object.keys(headerMap).forEach((excelHeader) => {
    const key = headerMap[excelHeader];
    if (!key) {
      return;
    }
    let value = row[excelHeader];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      value = String(value).trim();
      if (key === 'latitude' || key === 'longitude') {
        const num = parseFloat(value);
        payload[key] = Number.isFinite(num) ? num : null;
      } else if (key === 'ran_site_flag' || key === 'ola_location_flag') {
        payload[key] = value === '' ? null : value;
      } else {
        payload[key] = value;
      }
    }
  });
  return payload;
};

const toDisplayValue = (v) => (v == null || v === '' ? '--' : String(v));

const FiberPopBulkEditModal = ({ open, onClose, fiberPopData = [], onUpdateSuccess }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [headerMap, setHeaderMap] = useState(null);
  const [notInSystem, setNotInSystem] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileName('');
      setParsedRows([]);
      setHeaderMap(null);
      setNotInSystem([]);
      setValidRows([]);
      setErrorMessage('');
      setIsUpdating(false);
      setUpdateProgress({ current: 0, total: 0 });
    }
  }, [open]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    event.target.value = '';
    if (!selectedFile) {
      return;
    }

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      notify('error', 'Please upload only an Excel file (.xlsx or .xls).');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setErrorMessage('');
    setNotInSystem([]);
    setValidRows([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true });

        if (!jsonData.length) {
          setErrorMessage('File has no data rows.');
          setParsedRows([]);
          setHeaderMap(null);
          return;
        }

        const firstRow = jsonData[0];
        const rawHeaders = Object.keys(firstRow);
        const map = {};
        rawHeaders.forEach((h) => {
          const norm = normalizeHeader(h);
          const key = HEADER_TO_KEY[norm];
          if (key) {
            map[h] = key;
          }
        });

        if (!map || Object.keys(map).length === 0) {
          setErrorMessage(
            'No recognized columns found. Use headers like Fiber POP, Status, RAN Region, City, Latitude, Longitude, etc.'
          );
          setParsedRows([]);
          setHeaderMap(null);
          return;
        }

        const hasFiberPop = Object.values(map).includes('fiber_pop');
        if (!hasFiberPop) {
          setErrorMessage(
            'Sheet must contain a Fiber POP column (e.g. "Fiber POP", "FIBER_POP", "Fiber Pop").'
          );
          setParsedRows([]);
          setHeaderMap(null);
          return;
        }

        setHeaderMap(map);
        setParsedRows(jsonData);
      } catch (err) {
        console.error('Error reading Excel:', err);
        setErrorMessage('Error reading Excel file. Please check the file format.');
        setParsedRows([]);
        setHeaderMap(null);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  // Validate parsed rows against fiberPopData
  useEffect(() => {
    if (!parsedRows.length || !headerMap || !fiberPopData.length) {
      setNotInSystem([]);
      setValidRows([]);
      return;
    }

    const popMap = new Map();
    fiberPopData.forEach((pop) => {
      const fp = pop.fiber_pop;
      if (fp != null && fp !== '--') {
        const norm = String(fp).trim().toLowerCase();
        if (norm && !popMap.has(norm)) {
          popMap.set(norm, pop);
        }
      }
    });

    const missing = [];
    const valid = [];
    parsedRows.forEach((row) => {
      const rawFiberPop = row[Object.keys(headerMap).find((h) => headerMap[h] === 'fiber_pop')];
      const fiberPopVal = rawFiberPop != null ? String(rawFiberPop).trim() : '';
      if (!fiberPopVal) {
        return;
      }
      const norm = fiberPopVal.toLowerCase();
      const existing = popMap.get(norm);
      if (existing) {
        valid.push({ row, existing });
      } else {
        missing.push(fiberPopVal);
      }
    });

    setNotInSystem(missing);
    setValidRows(valid);
  }, [parsedRows, headerMap, fiberPopData]);

  const canConfirm = validRows.length > 0;
  const hasMissing = notInSystem.length > 0;

  const handleConfirmUpdate = async () => {
    if (!canConfirm) {
      return;
    }

    setIsUpdating(true);
    setUpdateProgress({ current: 0, total: validRows.length });

    const updatedRows = [];
    let done = 0;

    for (const { row, existing } of validRows) {
      const fromSheet = normalizeRowToPayload(row, headerMap);
      const payload = {
        fiber_pop: existing.fiber_pop,
        latest_status: existing.latest_status !== '--' ? existing.latest_status : null,
        ran_region: existing.ran_region !== '--' ? existing.ran_region : null,
        foo_region: existing.foo_region !== '--' ? existing.foo_region : null,
        city_name: existing.city_name !== '--' ? existing.city_name : null,
        type: existing.type !== '--' ? existing.type : null,
        telecom_region: existing.telecom_region !== '--' ? existing.telecom_region : null,
        ran_site_flag: existing.ran_site !== '--' ? existing.ran_site : null,
        ola_location_flag: existing.ola_location !== '--' ? existing.ola_location : null,
        site_name: existing.site_name !== '--' ? existing.site_name : null,
        technology: existing.technology !== '--' ? existing.technology : null,
        vendor: existing.vendor !== '--' ? existing.vendor : null,
        route: existing.route !== '--' ? existing.route : null,
        latitude: existing.latt != null ? existing.latt : null,
        longitude: existing.long != null ? existing.long : null,
      };
      Object.keys(fromSheet).forEach((k) => {
        if (fromSheet[k] !== undefined) {
          payload[k] = fromSheet[k];
        }
      });
      if (!payload.fiber_pop && existing.fiber_pop !== '--') {
        payload.fiber_pop = existing.fiber_pop;
      }
      try {
        const result = await updateFiberPopSite(existing.id, payload);
        if (result != null && (result?.status === 'success' || result?.response != null)) {
          updatedRows.push({
            id: existing.id,
            fiber_pop: payload.fiber_pop ?? existing.fiber_pop,
            latest_status: toDisplayValue(payload.latest_status),
            ran_region: toDisplayValue(payload.ran_region),
            foo_region: toDisplayValue(payload.foo_region),
            city_name: toDisplayValue(payload.city_name),
            type: toDisplayValue(payload.type),
            telecom_region: toDisplayValue(payload.telecom_region),
            ran_site: toDisplayValue(payload.ran_site_flag),
            ola_location: toDisplayValue(payload.ola_location_flag),
            site_name: toDisplayValue(payload.site_name),
            technology: toDisplayValue(payload.technology),
            vendor: toDisplayValue(payload.vendor),
            route: toDisplayValue(payload.route),
            latt: payload.latitude != null ? payload.latitude : existing.latt,
            long: payload.longitude != null ? payload.longitude : existing.long,
          });
        }
      } catch (e) {
        // continue with next row
      }
      done += 1;
      setUpdateProgress({ current: done, total: validRows.length });
    }

    setIsUpdating(false);
    notify('success', `Updated ${updatedRows.length} of ${validRows.length} Fiber POP(s).`);
    if (updatedRows.length && onUpdateSuccess) {
      onUpdateSuccess(updatedRows);
    }
    onClose?.();
  };

  const canClose = !isUpdating;

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Bulk Edit
        </Typography>
        {canClose && (
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an Excel file with columns like <strong>Fiber POP</strong> (required), Status, RAN
          Region, City, Latitude, Longitude, etc. Column names can be in any case or use underscores
          (e.g. Fiber POP, FIBER_POP, LATTITUDE, Latitude).
        </Typography>

        <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }} disabled={isUpdating}>
          {fileName || 'Choose Excel file (.xlsx, .xls)'}
          <input
            type="file"
            hidden
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
          />
        </Button>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {parsedRows.length > 0 && headerMap && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{validRows.length}</strong> row(s) match existing Fiber POPs.
            </Typography>
            {hasMissing && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  These Fiber POPs are not in our system and will be skipped:
                </Typography>
                <Typography variant="body2" component="span">
                  {notInSystem.slice(0, 15).join(', ')}
                  {notInSystem.length > 15 ? ` (and ${notInSystem.length - 15} more)` : ''}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {isUpdating && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
              Updating {updateProgress.current} of {updateProgress.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                updateProgress.total ? (updateProgress.current / updateProgress.total) * 100 : 0
              }
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={!canClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ bgcolor: '#BD1D23', '&:hover': { bgcolor: '#8B1519' } }}
          startIcon={<EditOutlinedIcon />}
          onClick={handleConfirmUpdate}
          disabled={!canConfirm || isUpdating}
        >
          {isUpdating ? 'Updating…' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiberPopBulkEditModal;
